/**
 * ProductCard — Displays a single pricing product as a card.
 * Shows product name, family/term/payment badges, eligibility badge,
 * near-par rate metrics, total adjustment, and an expandable rate grid.
 *
 * Props:
 *   product          — product object from pricing engine
 *   loanAmount       — effective loan amount for payment calcs
 *   selectedRate     — currently selected rate object (for highlighting)
 *   onSelectRate     — callback(rateObj) when a rate row is clicked
 *   isExpanded       — whether rate grid is expanded
 *   onToggleExpand   — callback() to toggle expansion
 *   showAllRates     — boolean for rate grid
 *   onToggleShowAll  — callback() for rate grid
 *   compensationType — 'LPC' | 'BPC'
 *   familyLimit      — optional { warningType, countyLimitAmount } for county limit badge
 */
import { Box, Button, Card, CardContent, CardHeader, Chip, Collapse, Divider, Stack, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StatusBadge, { ELIGIBILITY_MAP } from '../StatusBadge';
import RateGrid from './RateGrid';
import { fmt, fmtDollar, fmtPct, calcMonthlyPI, calcMonthlyIO, getAttributeChipColor } from './pricingUtils';
import { NearParRateLabel } from './NearParIndicators';
import IneligibleReasonsPanel from './IneligibleReasonsPanel';
import { filterIneligibleReasonStrings, hasDisplayableEligibilityAnalysis } from '../../../utils/ineligibleReasons';

const ProductCard = ({
  product: p,
  loanAmount,
  selectedRate,
  onSelectRate,
  isExpanded = false,
  onToggleExpand,
  showAllRates = false,
  onToggleShowAll,
  compensationType = 'LPC',
  familyLimit,
}) => {
  const parRateInfo = p.parRate;
  const isIO = p.paymentType === 'Interest Only';
  const monthlyPayment = parRateInfo
    ? (isIO ? calcMonthlyIO(parRateInfo.rate, loanAmount) : calcMonthlyPI(parRateInfo.rate, loanAmount, p.term))
    : null;
  const isSelectedProduct = selectedRate?.productShortName === p.productShortName;
  const displayIneligibleReasons = filterIneligibleReasonStrings(p.ineligibleReasons || []);

  const limitBadgeVariant = familyLimit?.warningType === 'error'
    ? 'danger'
    : familyLimit?.warningType === 'warning'
      ? 'warning'
      : 'success';

  const familyChipColor = getAttributeChipColor(p.family);
  const termChipColor = getAttributeChipColor(`${p.term}yr ${p.typeName || ''}`.trim());
  const paymentChipColor = getAttributeChipColor(p.paymentType);

  return (
    <Card
      variant="outlined"
      sx={{
        opacity: p.isEligible ? 1 : 0.85,
        overflow: 'hidden',
        borderColor: !p.isEligible ? 'error.main' : isSelectedProduct ? 'primary.main' : 'divider',
        borderWidth: isSelectedProduct ? 2 : 1,
      }}
    >
      <CardHeader
        sx={{ pb: 1, '& .MuiCardHeader-content': { minWidth: 0 } }}
        title={
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.25 }}
              >
                {p.productName}
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5, rowGap: 0.5 }}>
                <Chip label={p.family} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: familyChipColor.bg, color: familyChipColor.color }} />
                <Chip label={`${p.term}yr ${p.typeName}`} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: termChipColor.bg, color: termChipColor.color }} />
                {p.paymentType && (
                  <Chip label={p.paymentType} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: paymentChipColor.bg, color: paymentChipColor.color }} />
                )}
                {familyLimit && familyLimit.countyLimitAmount > 0 && (
                  <Chip
                    label={`County Limit: ${fmtDollar(familyLimit.countyLimitAmount)}`}
                    size="small"
                    sx={(theme) => ({
                      height: 20,
                      fontSize: '0.65rem',
                      bgcolor: theme.vars.palette.badges[limitBadgeVariant].bg,
                      color: theme.vars.palette.badges[limitBadgeVariant].color,
                    })}
                  />
                )}
              </Stack>
            </Box>
            <StatusBadge status={p.isEligible ? 'Eligible' : 'Ineligible'} map={ELIGIBILITY_MAP} />
          </Stack>
        }
        disableTypography
      />

      <CardContent sx={{ pt: 0 }}>
        {p.isEligible && parRateInfo ? (
          <>
            {/* Near-par rate metrics */}
            <Stack direction="row" justifyContent="space-around" textAlign="center" sx={{ mb: 2 }}>
              <Box>
                <NearParRateLabel />
                <Typography variant="h5" fontWeight={700}>{fmtPct(parRateInfo.rate)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Price</Typography>
                <Typography variant="h5" fontWeight={700}>{fmt(parRateInfo.adjustedPrice, 3)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{isIO ? 'Est. IO' : 'Est. P&I'}</Typography>
                <Typography variant="h5" fontWeight={700}>{monthlyPayment ? fmtDollar(monthlyPayment) : '—'}</Typography>
              </Box>
            </Stack>

            {/* Total adjustment */}
            {p.adjustments.length > 0 && (
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Total Adjustment {compensationType === 'LPC' && (
                    <Typography component="span" variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>(incl. compensation)</Typography>
                  )}
                </Typography>
                <Typography variant="caption" fontWeight={700} sx={{ color: p.totalAdjustment > 0 ? 'error.main' : p.totalAdjustment < 0 ? 'success.main' : 'text.primary' }}>
                  {p.totalAdjustment > 0 ? '-' : p.totalAdjustment < 0 ? '+' : ''}{fmt(Math.abs(p.totalAdjustment), 3)} pts
                </Typography>
              </Stack>
            )}

            {/* Expand rate grid button */}
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              fullWidth
              onClick={onToggleExpand}
              startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {isExpanded ? 'Hide Rate Grid' : `View Rate Grid (${p.prices.length} rates)`}
            </Button>
          </>
        ) : (
          <Box>
            {hasDisplayableEligibilityAnalysis(p.eligibilityAnalysis) ? (
              <IneligibleReasonsPanel analysis={p.eligibilityAnalysis} />
            ) : displayIneligibleReasons.length > 0 ? (
              <Box component="ul" sx={{ pl: 2, mb: 0, color: 'error.main', fontSize: '0.8rem' }}>
                {displayIneligibleReasons.map((r, i) => <li key={i}>{r}</li>)}
              </Box>
            ) : null}
            {p.error && <Typography variant="caption" color="error">{p.error}</Typography>}
          </Box>
        )}
      </CardContent>

      {/* Expanded rate grid */}
      <Collapse in={isExpanded && p.isEligible}>
        <Divider />
        <CardContent>
          <RateGrid
            product={p}
            loanAmount={loanAmount}
            selectedRate={selectedRate}
            onSelectRate={onSelectRate}
            showAllRates={showAllRates}
            onToggleShowAll={onToggleShowAll}
          />
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default ProductCard;
